import { ProgramsConfig } from 'fitbeat.gym-core';
import { BaseUserProfile, CardioOrResistance, GymTypes } from 'fitbeat.models';
import React, { FunctionComponent } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Theme, { ThemeType } from '../../theme';
import { FeatureToggle } from '../../ui/components';
import BuildIcon from '../../assets/icons/activity-types/functional-strength-outlined.svg'
import BurnIcon from '../../assets/icons/activity-types/functional-cardio-outlined.svg'
import BalanceIcon from '../../assets/icons/activity-types/functional-pilates-outlined.svg'
import { i18n } from '../../utils';
import { defaultScale } from '../../utils/scaling';
import { themeStyleMapper } from '../steps/personaliseWorkoutSteps';
import { ButtonMargin, SelectionContainer } from './SelectionContainer';

interface IProps {
  workoutType: CardioOrResistance | null;
  theme: ThemeType;
  t(key: string): string;
  onPress(workoutType: CardioOrResistance): Promise<void>;
}

export const mappedWorkoutType = {
  [CardioOrResistance.Cardio]: {
    title: i18n.t('bookings:burn'),
    subtitle: i18n.t('bookings:burnSubtitle'),
    parentheticDescription: 'Cardio',
    icon: (color = Theme.BLACK) =>
      <BurnIcon stroke={color} color={color} width={defaultScale(35)} height={defaultScale(35)}/>,
  },
  [CardioOrResistance.Resistance]: {
    title: i18n.t('bookings:build'),
    subtitle: i18n.t('bookings:buildSubtitle'),
    parentheticDescription: 'Strength',
    icon: (color = Theme.BLACK) =>
      <BuildIcon stroke={color} color={color} width={defaultScale(35)} height={defaultScale(35)}/>,
  },
  [CardioOrResistance.Balance]: {
    title: i18n.t('bookings:balance'),
    subtitle: i18n.t('bookings:balanceSubtitle'),
    parentheticDescription: 'Pilates',
    icon: (color = Theme.BLACK) =>
      <BalanceIcon stroke={color} color={color} width={defaultScale(35)} height={defaultScale(35)} strokeWidth={2}/>,
  },
};

export const WorkoutTypeSelection: FunctionComponent<IProps> =
  ({ t, onPress, workoutType, theme = ThemeType.light, workoutSettings, gym}) => {
    const themeStyle = themeStyleMapper[theme];
    const settingsForDescription = {...workoutSettings, cardioOrResistance: workoutType};
    return (
      <View>
        <View style={styles.container}>
          <WorkoutType
            style={{ marginRight: ButtonMargin }}
            onPress={onPress}
            currentWorkoutType={workoutType}
            workoutType={CardioOrResistance.Cardio}
            theme={theme}
          />
          <WorkoutType
            onPress={onPress}
            style={{ marginLeft:ButtonMargin }}
            currentWorkoutType={workoutType}
            workoutType={CardioOrResistance.Resistance}
            theme={theme}
          />
            <WorkoutType
              onPress={onPress}
              style={{ marginLeft: ButtonMargin }}
              currentWorkoutType={workoutType}
              workoutType={CardioOrResistance.Balance}
              theme={theme}
            />
        </View>
        <Text style={[styles.subtitle, themeStyle.textColor2]}>
          {ProgramsConfig.getWorkoutSettingDescription(
            settingsForDescription,
            gym.type === GymTypes.Home,
            !!gym.bagContactAllowed,
          )}
        </Text>
      </View>
    );
  };

const WorkoutType = ({ currentWorkoutType, workoutType, onPress, style, theme }) => {
  const themeStyle = themeStyleMapper[theme];
  const isSelected = currentWorkoutType === workoutType;
  return (
    <View>
      <SelectionContainer
        width={defaultScale(110)}
        height={defaultScale(110)}
        title={mappedWorkoutType[workoutType].title}
        titleStyle={[styles.title, { color: isSelected ? themeStyle.themeColor1Inverse : themeStyle.themeColor1 }]}
        isSelected={isSelected}
        style={style}
        onPress={() => onPress(workoutType)}
        theme={theme}
        icon={(
          <View style={styles.iconContainer}>
            {mappedWorkoutType[workoutType].icon(isSelected ? themeStyle.themeColor1Inverse : themeStyle.themeColor1)}
          </View>
        )}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: defaultScale(10),
  },
  title: {
    ...Theme.textStyle.bodyCopyBold,
    paddingTop: defaultScale(10),
    textAlign: 'center',
  },
  subtitle: {
    ...Theme.textStyle.bodyCopyReg,
    paddingTop: defaultScale(10),
    paddingHorizontal: defaultScale(10),
    textAlign: 'center',
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    alignSelf: 'center',
    textAlign: 'center',
  },
});
